import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  CircularProgress,
  Alert,
  Link,
  Tooltip
} from '@mui/material';
import { CloudDownload as CloudDownloadIcon, Info as InfoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AllGeneratedImages = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user_email = localStorage.getItem('user_email');
    
    if (!token || !user_email) {
      navigate('/login');
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['x-email'] = user_email;
    
    fetchAllImages();
  }, [navigate]);

  const fetchAllImages = async () => {
    try {
      const response = await axios.get('/api/loras/all-generated-images');
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      setError('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (url) => {
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = url;
    // Extract filename from URL or use timestamp
    const filename = url.split('/').pop() || `generated-image-${Date.now()}.jpg`;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#369FA1' }}>
          All Generated Images
        </Typography>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ textDecoration: 'none', color: '#369FA1' }}
        >
          Back to Dashboard
        </Link>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {images.map((image) => (
          <Grid item xs={12} sm={6} md={4} key={image._id}>
            <Card>
              <CardMedia
                component="img"
                image={image.url}
                alt={`Generated with prompt: ${image.prompt}`}
                sx={{ height: 300, objectFit: 'cover' }}
              />
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {image.prompt}
                  </Typography>
                  <Tooltip title="View LoRA details">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/generate/${image.lora_id}`)}
                      sx={{ ml: 1 }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(image.created_at).toLocaleDateString()}
                  </Typography>
                  <IconButton
                    onClick={() => downloadImage(image.url)}
                    sx={{ color: '#369FA1' }}
                  >
                    <CloudDownloadIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {images.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No generated images found. Start by creating a LoRA and generating some images!
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default AllGeneratedImages; 